import React from "react";
import { MOOD_PRESETS } from "@/lib/moodPresets";

/**
 * Hero background = fotografia realistica di uno stadio (Unsplash) con
 * fari intermittenti tipo flash. Il velo bianco è renderizzato da Home.jsx
 * per garantire leggibilità dei testi sopra.
 *
 * `preset` (giorno/notte/allenamento) modula intensità e temperatura del filtro
 * ma la base resta sempre lo stadio.
 */
const STADIUM_URL =
  "https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=2000&q=70";

// 8 spot dei fari (percentuali su viewport). Il flash è casuale grazie a delay diversi.
const LIGHTS = [
  { x: 8,  y: 12, size: 220, delay: 0.0, dur: 3.6 },
  { x: 22, y: 6,  size: 260, delay: 1.1, dur: 4.4 },
  { x: 38, y: 10, size: 240, delay: 2.3, dur: 3.2 },
  { x: 52, y: 5,  size: 300, delay: 0.6, dur: 5.0 },
  { x: 66, y: 9,  size: 240, delay: 1.7, dur: 4.1 },
  { x: 80, y: 6,  size: 260, delay: 2.9, dur: 3.7 },
  { x: 92, y: 12, size: 220, delay: 0.4, dur: 4.6 },
  { x: 46, y: 20, size: 340, delay: 3.5, dur: 6.0 },
];

export default function PitchTacticalBackground({ preset = "notte" }) {
  const p = MOOD_PRESETS[preset] || MOOD_PRESETS.notte;
  // Filtri per adattare la foto al mood
  const filterByPreset = {
    giorno:      "brightness(1.08) saturate(1.05)",
    allenamento: "brightness(0.95) saturate(0.95) sepia(0.1)",
    notte:       "brightness(0.9) saturate(1.1)",
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid={`stadium-bg-${p.id}`}>
      {/* Stadium photo */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${STADIUM_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center 45%",
          filter: filterByPreset[p.id] || filterByPreset.notte,
        }}
      />

      {/* Dark blueish top → transparent bottom gradient per far respirare le luci */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, rgba(2,6,23,0.55) 0%, rgba(2,6,23,0.18) 40%, rgba(2,6,23,0.05) 100%)",
      }} />

      {/* Fari intermittenti */}
      {LIGHTS.map((l, i) => (
        <span
          key={i}
          className="stadium-flash absolute"
          style={{
            left: `${l.x}%`,
            top: `${l.y}%`,
            width: l.size,
            height: l.size,
            marginLeft: -l.size / 2,
            marginTop: -l.size / 2,
            animationDelay: `${l.delay}s`,
            animationDuration: `${l.dur}s`,
          }}
          aria-hidden
        />
      ))}

      {/* Micro-particelle (coriandoli) — leggero effetto festa */}
      <div className="stadium-confetti" aria-hidden />

      <style>{`
        .stadium-flash {
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,240,0.95) 0%, rgba(255,255,240,0.55) 25%, rgba(255,255,240,0) 65%);
          mix-blend-mode: screen;
          filter: blur(2px);
          opacity: 0;
          animation-name: stadiumFlash;
          animation-iteration-count: infinite;
          animation-timing-function: steps(1, end);
          pointer-events: none;
        }
        @keyframes stadiumFlash {
          0%   { opacity: 0; }
          2%   { opacity: 1; }
          6%   { opacity: 0.15; }
          10%  { opacity: 1; }
          14%  { opacity: 0; }
          40%  { opacity: 0; }
          44%  { opacity: 0.9; }
          48%  { opacity: 0.05; }
          52%  { opacity: 1; }
          56%  { opacity: 0; }
          100% { opacity: 0; }
        }
        .stadium-confetti {
          position: absolute; inset: 0;
          background-image:
            radial-gradient(2px 2px at 12% 22%, rgba(255,255,255,0.85) 50%, transparent 51%),
            radial-gradient(2px 2px at 28% 38%, rgba(255,200,120,0.85) 50%, transparent 51%),
            radial-gradient(2px 2px at 44% 12%, rgba(180,220,255,0.85) 50%, transparent 51%),
            radial-gradient(2px 2px at 62% 30%, rgba(255,180,180,0.85) 50%, transparent 51%),
            radial-gradient(2px 2px at 78% 18%, rgba(255,255,255,0.85) 50%, transparent 51%),
            radial-gradient(2px 2px at 90% 34%, rgba(255,230,120,0.85) 50%, transparent 51%);
          animation: confettiDrift 12s linear infinite;
          opacity: 0.55;
          mix-blend-mode: screen;
        }
        @keyframes confettiDrift {
          0%   { transform: translateY(-6px); }
          50%  { transform: translateY(4px); }
          100% { transform: translateY(-6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stadium-flash { animation: none; opacity: 0.65; }
          .stadium-confetti { animation: none; }
        }
      `}</style>
    </div>
  );
}
